import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import { useApi } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { SectionHead, DataBadge, Skel, ErrorState } from "../components/common";
import BubblePack from "../components/BubblePack";
import usePageMeta from "../lib/usePageMeta";

const turnoverColor = (t) => {
  if (!t || t <= 0) return "#26332c";
  const x = Math.min(1, Math.log10(1 + t * 100) / 2);
  const l = 16 + x * 26;
  return `hsl(160 ${Math.round(40 + x * 44)}% ${Math.round(l)}%)`;
};

function Cell(props) {
  const { x, y, width, height, symbol, price, turnover, nav, address } = props;
  if (width < 4 || height < 4) return null;
  return (
    <g onClick={() => address && nav(`/token/${address}`)} className="cursor-pointer" data-testid={`heatmap-cell-${symbol}`}>
      <rect x={x} y={y} width={width} height={height} fill={turnoverColor(turnover)} stroke="#0A0A0A" strokeWidth={1.5} />
      {width > 52 && height > 34 && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 4} textAnchor="middle" fill="#F3F4F6" fontSize={Math.min(16, width / 6)} fontFamily="JetBrains Mono, monospace" fontWeight="600">{symbol}</text>
          <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" fill="#C9CDD3" fontSize={Math.min(11, width / 9)} fontFamily="JetBrains Mono, monospace">{price != null ? fmtUsd(price) : ""}</text>
        </>
      )}
    </g>
  );
}

export default function Heatmap() {
  usePageMeta("Market Heatmap", "Coin360-style treemap and bubble map of Robinhood Chain tokens sized by market cap and colored by live trading activity.");
  const [scope, setScope] = useState("tokens");
  const [sizeBy, setSizeBy] = useState("market_cap");
  const [view, setView] = useState("treemap");
  const { data: tokens } = useApi("/tokens?sort=volume");
  const { data: stocks } = useApi("/stocks");
  const nav = useNavigate();

  const source = scope === "stocks" ? stocks : tokens;
  const items = useMemo(() => {
    const rows = (source?.items || [])
      .map((t) => ({
        ...t,
        size: Math.max(sizeBy === "volume" ? t.volume_24h || 0 : t.market_cap || 0, 0),
        turnover: t.market_cap ? (t.volume_24h || 0) / t.market_cap : 0,
      }))
      .filter((t) => t.size > 0)
      .sort((a, b) => b.size - a.size)
      .slice(0, 49);
    return rows;
  }, [source, sizeBy]);

  const bubbleItems = items.map((t) => ({
    id: t.address, label: t.symbol, value: t.size, color: turnoverColor(t.turnover),
    tooltip: t.name, sub: `${t.price != null ? fmtUsd(t.price) : ""} · ${sizeBy === "volume" ? "Vol" : "MCap"} ${fmtUsd(t.size)}`,
  }));

  return (
    <div className="space-y-3">
      <SectionHead title="Market Heatmap" badge={<DataBadge live={source?.live ?? true} />} right={
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-px t-card p-0.5">
            {[["treemap", "Treemap"], ["bubbles", "Bubbles"]].map(([k, l]) => (
              <button key={k} data-testid={`view-${k}`} onClick={() => setView(k)}
                className={`text-[11px] px-2.5 py-1 rounded-sm transition-colors ${view === k ? "bg-[#059669] text-white" : "text-[#9CA3AF] hover:bg-[#1A1A1A]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-px t-card p-0.5">
            {[["tokens", "Tokens"], ["stocks", "Stock Tokens"]].map(([k, l]) => (
              <button key={k} data-testid={`scope-${k}`} onClick={() => setScope(k)}
                className={`text-[11px] px-2.5 py-1 rounded-sm transition-colors ${scope === k ? "bg-[#059669] text-white" : "text-[#9CA3AF] hover:bg-[#1A1A1A]"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-px t-card p-0.5">
            {[["market_cap", "Size: MCap"], ["volume", "Size: Volume"]].map(([k, l]) => (
              <button key={k} data-testid={`sizeby-${k}`} onClick={() => setSizeBy(k)}
                className={`text-[11px] px-2.5 py-1 rounded-sm transition-colors ${sizeBy === k ? "bg-[#1A1A1A] text-[#F3F4F6]" : "text-[#6B7280] hover:text-[#9CA3AF]"}`}>{l}</button>
            ))}
          </div>
        </div>
      } />
      <p className="text-xs text-[#6B7280] -mt-1">
        Cell size = {sizeBy === "volume" ? "24h onchain volume" : "onchain market cap"}. Color intensity = 24h turnover (volume ÷ market cap) — brighter green means more active trading. Click any cell to open the token.
      </p>

      {!source ? <Skel rows={6} h={60} /> : !source.live && !items.length ? <ErrorState /> : (
        <div className="t-card p-1" data-testid="heatmap-container">
          {view === "treemap" ? (
            <div className="h-[560px]">
              <ResponsiveContainer width="100%" height="100%">
                <Treemap data={items.map((t) => ({ name: t.symbol, size: t.size, symbol: t.symbol, price: t.price, turnover: t.turnover, address: t.address, nav }))}
                  dataKey="size" aspectRatio={4 / 3} isAnimationActive={false} content={<Cell />}>
                  <Tooltip content={({ payload }) => payload?.[0] ? (
                    <div className="bg-[#121212] border border-[#262626] rounded-sm px-2.5 py-1.5 text-xs">
                      <div className="font-medium">{payload[0].payload.symbol}</div>
                      <div className="num text-[#9CA3AF]">{payload[0].payload.price != null ? fmtUsd(payload[0].payload.price) : ""} · {fmtUsd(payload[0].payload.size)}</div>
                    </div>
                  ) : null} />
                </Treemap>
              </ResponsiveContainer>
            </div>
          ) : (
            <BubblePack items={bubbleItems} height={560} onClick={(d) => nav(`/token/${d.id}`)} />
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
        <span>Turnover:</span>
        {[0, 0.02, 0.1, 0.4, 1].map((t) => <span key={t} className="inline-block w-6 h-3 rounded-[2px]" style={{ background: turnoverColor(t) }} />)}
        <span>low → high</span>
      </div>
    </div>
  );
}
